import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import {catchError, debounceTime, distinctUntilChanged, Observable, of, Subject, switchMap, takeUntil} from "rxjs";
import { DuplicatePostalCodeValidator, ValidateSimpleLocation } from "./location-multiple-large-input.validator";
import { CitySuggestion, SimpleLocation } from "../../../../platform-pages/src/lib/api/google-geocoding/google-geocoding.types";
import { CmsNameValue, compareCmsNameValue } from "../../../../platform-pages/src/lib/api/cms/cms.types";
import { CmsCachedService } from "../../../../platform-pages/src/lib/api/cms/cms-cached.service";
import { GoogleGeocodingService } from "../../../../platform-pages/src/lib/api/google-geocoding/google-geocoding.service";

@Component({
  selector: "mh-location-multiple-large-input",
  templateUrl: "./location-multiple-large-input.component.html",
  styleUrls: ["./location-multiple-large-input.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => LocationMultipleLargeInputComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: LocationMultipleLargeInputComponent, multi: true },
  ],
})
export class LocationMultipleLargeInputComponent implements OnDestroy, ControlValueAccessor, OnInit, AfterViewInit {
  @Input() value!: Array<SimpleLocation>;
  @Input() showAddBtn = true;
  private destroy$ = new Subject<void>();
  public radiusOptions$: Observable<Array<CmsNameValue>>;
  form!: UntypedFormGroup;
  public cityOptions: CitySuggestion[] = [];
  public zipOptions: SimpleLocation[] = [];
  private citySearch$ = new Subject<string>();
  private zipSearch$ = new Subject<string>();
  private selectedCountryCode?: string;
  private suppressLocationSelection = false;
  zipFocused = false;
  disabled = false;
  public loading= false;
  public cityLoading = false;
  public zipLoading = false;
  public nzFilterOption = (): boolean => true;

  get countryControl(): AbstractControl | null {
    return this.form.get("country");
  }

  constructor(
    private formBuilder: FormBuilder,
    private geocodingService: GoogleGeocodingService,
    private cmsCachedService: CmsCachedService,
    private cd: ChangeDetectorRef,
  ) {
    this.radiusOptions$ = this.cmsCachedService.getUserDataByName("radius", false);
  }

  addLocation() {
    if (this.form.invalid || !this.countryControl?.getRawValue()) {
      Object.values(this.form.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }
    this.value = [...(this.value || []), { ...this.form.getRawValue(), country: this.countryControl?.getRawValue() }];
    this.form.reset();
    this.cityOptions = [];
    this.zipOptions = [];
    this.notifyOfChange();
  }

  editLocation(location: SimpleLocation, index: number) {
    this.value = this.value.filter((_, itemIndex) => itemIndex !== index);
    this.cityOptions = location.city
      ? [{ placeId: location.geocoderResult || "", city: location.city, label: location.formattedAddress || location.city }]
      : [];
    this.zipOptions = location.postalCode ? [location] : [];
    this.selectedCountryCode = location.countryCode;
    this.form.reset(location);
    this.notifyOfChange();
  }

  deleteLocation(location: SimpleLocation, index: number) {
    this.value = this.value.filter((_, itemIndex) => itemIndex !== index);
    this.notifyOfChange();
  }

  ngOnInit() {
    this.createForm();
  }

  ngAfterViewInit() {
    this.updatePostalCodeValidator();
  }

  isInvalid(location: SimpleLocation): boolean {
    return !ValidateSimpleLocation(location);
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  private createForm() {
    this.form = this.formBuilder.group({
      postalCode: [undefined, [DuplicatePostalCodeValidator(this.value)]],
      city: [undefined, [Validators.required]],
      country: [undefined, [Validators.required]],
      radius: [undefined, []],
    });
    this.form.get("postalCode")?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((postalCode) => {
      if (postalCode) {
        this.onZipSelected(postalCode);
      } else {
        this.clearLocationFromZip();
      }
    });
    this.form.get("city")?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((city) => {
      if (city) {
        this.onCitySelected(city);
      } else {
        this.clearLocationFromCity();
      }
    });
    this.form.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(500), distinctUntilChanged((prev, curr) => prev.postalCode === curr.postalCode)).subscribe((value) => {
      this.handleZipChange();
    });

    this.citySearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query) => {
        if (!query || query.trim().length < 2) return of(null);
        this.cityLoading = true;
        return this.geocodingService.searchCities(query, this.selectedCountryCode).pipe(catchError(() => of([])));
      }),
      takeUntil(this.destroy$),
    ).subscribe((location) => {
      this.cityOptions = location || [];
      this.cityLoading = false;
      this.cd.markForCheck();
    });

    this.zipSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query) => {
        if (!query || query.trim().length < 2) return of([]);
        this.zipLoading = true;
        return this.geocodingService.decodeAddresses(query).pipe(catchError(() => of([])));
      }),
      takeUntil(this.destroy$),
    ).subscribe((locations) => {
      this.zipOptions = this.uniqueZipOptions(locations);
      this.zipLoading = false;
      this.cd.markForCheck();
    });
  }

  searchCity(query: string): void {
    this.citySearch$.next(query);
  }

  searchZip(query: string): void {
    const country = this.countryControl?.value;
    const city = this.form?.get("city")?.value;
    const context = [city, country].filter(Boolean).join(", ");
    this.zipSearch$.next(context ? `${query}, ${context}` : query);
  }

  onCountryCodeSelected(countryCode: string): void {
    if (this.suppressLocationSelection) return;
    this.selectedCountryCode = countryCode;
    this.cityOptions = [];
    this.zipOptions = [];
    this.form.patchValue({ city: undefined, postalCode: undefined }, { emitEvent: false });
    this.cd.markForCheck();
  }

  onCitySelected(city: string): void {
    if (this.suppressLocationSelection) return;
    const selectedCity = this.cityOptions.find((option) => option.city === city);
    if (!selectedCity) return;

    this.cityLoading = true;
    this.geocodingService.decodeAddresses(selectedCity.label).pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$),
    ).subscribe((locations) => {
      if (!locations?.length) {
        const country = selectedCity.country || selectedCity.label.split(",").pop()?.trim();
        this.suppressLocationSelection = true;
        this.form.patchValue({ city: selectedCity.city, country }, { emitEvent: false });
        this.suppressLocationSelection = false;
        this.markLocationControlsClean();
        this.cityLoading = false;
        this.cd.markForCheck();
        return;
      }

      const location = locations.find((item) => !!item.country) || locations[0];
      const country = location.country || selectedCity.country || selectedCity.label.split(",").pop()?.trim();
      this.selectedCountryCode = location.countryCode;
      this.zipOptions = this.uniqueZipOptions(locations);
      this.suppressLocationSelection = true;
      this.form.patchValue({
        city: location.city || selectedCity.city,
        postalCode: undefined,
        country,
      }, { emitEvent: false });
      this.markLocationControlsClean();
      this.suppressLocationSelection = false;
      this.cityOptions = [{
        ...selectedCity,
        city: location.city || selectedCity.city,
      }];
      this.onChange({ ...location, country, ...this.form.getRawValue() });
      this.cityLoading = false;
      this.cd.markForCheck();
    });
  }

  onZipSelected(postalCode: string): void {
    const selectedZip = this.zipOptions.find((option) => option.postalCode === postalCode);
    if (!selectedZip) return;

    this.selectedCountryCode = selectedZip.countryCode;
    this.cityOptions = selectedZip.city
      ? [{ placeId: selectedZip.geocoderResult || "", city: selectedZip.city, label: selectedZip.formattedAddress || selectedZip.city }]
      : [];
    this.suppressLocationSelection = true;
    this.form.patchValue({
      postalCode: selectedZip.postalCode,
      city: selectedZip.city,
      country: selectedZip.country,
    }, { emitEvent: false });
    this.markLocationControlsClean();
    this.suppressLocationSelection = false;
    this.onChange({ ...selectedZip, ...this.form.getRawValue() });
    this.cd.markForCheck();
  }

  private clearLocationFromZip(): void {
    this.suppressLocationSelection = true;
    this.form.patchValue({ city: undefined, country: undefined }, { emitEvent: false });
    this.suppressLocationSelection = false;
    this.cityOptions = [];
    this.zipOptions = [];
    this.selectedCountryCode = undefined;
    this.cd.markForCheck();
  }

  private clearLocationFromCity(): void {
    this.suppressLocationSelection = true;
    this.form.patchValue({ postalCode: undefined, country: undefined }, { emitEvent: false });
    this.suppressLocationSelection = false;
    this.cityOptions = [];
    this.zipOptions = [];
    this.selectedCountryCode = undefined;
    this.cd.markForCheck();
  }

  private markLocationControlsClean(): void {
    ["city", "country"].forEach((controlName) => {
      const control = this.form.get(controlName);
      control?.markAsPristine();
      control?.markAsUntouched();
      control?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      if (control?.value) control.setErrors(null);
    });
    this.cd.markForCheck();
  }

  private uniqueZipOptions(locations: SimpleLocation[]): SimpleLocation[] {
    return locations.reduce((options, location) => {
      if (!location.postalCode) return options;
      const existingIndex = options.findIndex((option) => option.postalCode === location.postalCode);
      if (existingIndex < 0) return [...options, location];
      const existing = options[existingIndex];
      const existingScore = Number(!!existing.city) + Number(!!existing.country);
      const locationScore = Number(!!location.city) + Number(!!location.country);
      if (locationScore > existingScore) options[existingIndex] = location;
      return options;
    }, [] as SimpleLocation[]);
  }

  private updatePostalCodeValidator() {
    this.form.get('postalCode')?.setValidators([DuplicatePostalCodeValidator(this.value)]);
    this.form.get('postalCode')?.updateValueAndValidity();
  }

  handleZipChange() {
    const zipControl = this.form.get("postalCode");
    const cityControl = this.form.get("city");
    const countryControl = this.countryControl;
    if (zipControl?.valid && this.zipFocused) {
      this.loading = true;
      this.cd.markForCheck();
      this.geocodingService.decodeAddress(zipControl.value).subscribe((location) => {
        countryControl?.patchValue(location.country, { emitEvent: false });
        cityControl?.patchValue(location.city, { emitEvent: false });
        this.selectedCountryCode = location.countryCode;
        this.zipOptions = location.postalCode ? [location] : [];
        this.cityOptions = location.city
          ? [{ placeId: location.geocoderResult || "", city: location.city, label: location.formattedAddress || location.city }]
          : [];
        this.onChange({ ...location, ...this.form.getRawValue() });
        this.loading = false;
      });
    }
  }

  private notifyOfChange() {
    if (this.onChange) {
      this.updatePostalCodeValidator();
      this.onChange(this.value.filter((location) => !this.isInvalid(location)));
    }
  }

  onChange = (value: Array<SimpleLocation>) => {};
  onTouched = () => {};

  writeValue(value: Array<SimpleLocation>) {
    this.value = value;
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    if(isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
      this.updatePostalCodeValidator()
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if ((!this.value || this.value.length == 0) && control.dirty) {
      Object.values(this.form.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return { required: true };
    }
    const invalidItems = this.value.filter((item) => !ValidateSimpleLocation(item));
    return invalidItems.length > 0 ? { required: true } : null;
  }

  handleZipBlur() {
    setTimeout(() => {
      this.zipFocused = false;
    }, 1000);
  }

  protected readonly compareCmsNameValue = compareCmsNameValue;
}
